import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PublicShareInfo } from '@simultrain/shared';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/store.js';
import { LoginPage } from './Login.js';
import { ChapterRunnerPage } from './ChapterRunner.js';

export function SharedChapterPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const user = useAuth((s) => s.user);
  const [info, setInfo] = useState<PublicShareInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  // A link opened cold has had zero clicks on the page yet, unlike the
  // normal login → project → chapter → mode-button path — so the runner's
  // very first auto-played narration would get silently blocked by the
  // browser's autoplay policy. One click here (on any element, doesn't
  // need to touch the <audio> itself) satisfies that requirement for the
  // rest of the page's lifetime.
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!token) return;
    setInfo(null);
    setNotFound(false);
    void api
      .getPublicShareInfo(token)
      .then(setInfo)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) setNotFound(true);
      });
  }, [token]);

  if (!token) return null;

  if (notFound) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="card" style={{ width: 360 }}>
          <div className="error-banner">{t('shared.notFound')}</div>
        </div>
      </div>
    );
  }

  if (!info) return null;

  // The login form itself re-renders this page once it succeeds (user
  // becomes truthy in the shared auth store) — no redirect needed here.
  if (info.shareMode === 'authenticated' && !user) {
    return (
      <>
        <div
          className="muted"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, textAlign: 'center', padding: 8, fontSize: 13 }}
        >
          {t('shared.loginRequired')}
        </div>
        <LoginPage />
      </>
    );
  }

  if (!started) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="card" style={{ width: 360, textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}>{info.projectName}</div>
          <h2 style={{ marginTop: 0 }}>{info.chapterTitle}</h2>
          <button style={{ width: '100%', marginTop: 12 }} onClick={() => setStarted(true)}>
            {t('shared.start')}
          </button>
        </div>
      </div>
    );
  }

  return <ChapterRunnerPage chapterId={info.chapterId} mode="instruction" shareToken={token} />;
}
